import { uniq } from "lodash";

import displayCacheSelectors from "../../selectors";

const areRelevantValuesSame = (newItem, prevItem) =>
  newItem &&
  prevItem &&
  newItem.startDate === prevItem.startDate &&
  newItem.duration === prevItem.duration &&
  newItem.preferredStartDate === prevItem.preferredStartDate &&
  newItem.preferredDuration === prevItem.preferredDuration &&
  newItem.latestStartDate === prevItem.latestStartDate &&
  newItem.durationToEarliestEnd === prevItem.durationToEarliestEnd &&
  newItem.ongoing === prevItem.ongoing &&
  newItem.blockingDependencies === prevItem.blockingDependencies &&
  newItem.blockedByDependencies === prevItem.blockedByDependencies &&
  newItem.dateConstraints === prevItem.dateConstraints;

// What will affect violated date constraints?

// Change of a constraint <-- need to test the 2 relevant items
// Change of a dependency <-- need to test the 2 relevant items
// (newAndChangedIdSet has this) Removal of a dependency/constraint <-- need to test the 2 relevant items

// On Data Load <-- need to test everything

// Changes: everything in ItemDatesRecord
// will probably eventually consider splitting that out into its own object?
const updateIsViolated = (
  state,
  newAndChangedIdSet,
  todayDate,
  newItemsById,
  prevItemsById,
  newDependenciesById,
  prevDependenciesById,
  newConstraintsById,
  prevConstraintsById,
  newGlobalState
) => {
  // We are only interested in changes to an item's date, or changes to a descendant's ongoing/date
  // which are all inside ItemRecords.
  // So if there are no changed ItemRecords, we do not need to calculate anything.
  if (
    newAndChangedIdSet.size === 0 &&
    newDependenciesById === prevDependenciesById &&
    newConstraintsById === prevConstraintsById
  ) {
    return state;
  }

  // For items with changed dates:
  // need to test themselves, and any linked items
  const dateChangedIds = newAndChangedIdSet.filter(
    cId => !areRelevantValuesSame(newItemsById.get(cId), prevItemsById.get(cId))
  );

  const dataLinkedIds = dateChangedIds.reduce((result, cId) => {
    const currItem = newItemsById.get(cId);
    const blockingIds = currItem.blockingDependencies.map(depId => newDependenciesById.get(depId).object);
    const blockedByIds = currItem.blockedByDependencies.map(depId => newDependenciesById.get(depId).subject);
    const constraintIds = currItem.dateConstraints
      .map(conId => {
        const constraint = newConstraintsById.get(conId);
        return constraint.subject === cId ? constraint.object : constraint.subject;
      })
      .filter(v => v);
    result.push(...blockingIds.toArray());
    result.push(...blockedByIds.toArray());
    result.push(...constraintIds.toArray());
    return result;
  }, []);

  // For dependencies that have changed, need to test the connected items
  // We only need to deal with dependencies that already existed, since any additions/deletions
  // will be taken care of already
  const dependencyChangeLinkedIds = newDependenciesById
    .filter((dep, depId) => prevDependenciesById.has(depId) && dep !== prevDependenciesById.get(depId))
    .reduce((result, dep) => {
      result.push(dep.subject);
      result.push(dep.object);
      return result;
    }, []);

  const constraintChangeLinkedIds = newConstraintsById
    .filter((con, conId) => prevConstraintsById.has(conId) && con !== prevConstraintsById.get(conId))
    .reduce((result, con) => {
      result.push(con.subject);
      if (con.object) {
        result.push(con.object);
      }
      return result;
    }, []);

  const affectedIds = uniq([
    ...dateChangedIds.toArray(),
    ...dataLinkedIds,
    ...dependencyChangeLinkedIds,
    ...constraintChangeLinkedIds
  ]);

  // If there are no IDs to check, let's bail
  if (affectedIds.length === 0) {
    return state;
  }

  const violatedConstraints = displayCacheSelectors.getViolatedDateConstraints(newGlobalState);
  const violatedDependencies = displayCacheSelectors.getViolatedDependencies(newGlobalState);

  // Now, for every testId we will call getIndividualItemDates
  return state.withMutations(s => {
    affectedIds.forEach(cId => {
      const itemRec = state.get(cId);

      const hasViolatedDateConstraints = violatedConstraints
        ? Boolean(violatedConstraints.find(c => c.subject === cId || c.object === cId))
        : false;
      const hasViolatedDependencies = violatedDependencies
        ? Boolean(violatedDependencies.find(c => c.subject === cId || c.object === cId))
        : false;

      const hasViolatedConstraints = hasViolatedDateConstraints || hasViolatedDependencies;

      if (hasViolatedConstraints !== itemRec.hasViolatedConstraints) {
        s.update(cId, d => d.set("hasViolatedConstraints", hasViolatedConstraints));
      }
    });
  });
};

export default updateIsViolated;
